import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye, Cpu, Monitor, MemoryStick, Pencil, Trash2, Star, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { productsAPI } from '../../lib/api';
import { formatPrice, calculateDiscount, cn, getStockStatus, isProductAvailable } from '../../lib/utils';
import { productUrl } from '../../lib/urlHelpers';
import { toast } from 'sonner';
import { ProductEditModal } from '../admin/ProductEditModal';

// Helper to extract key specs for display
function getDisplaySpecs(product) {
    const specs = product.specs || {};
    const hiddenSpecs = product.hidden_specs || {};
    const allSpecs = { ...specs, ...hiddenSpecs };
    const name = product.name?.toLowerCase() || '';
    
    const displaySpecs = [];
    
    // CPU spec
    const cpuSpec = allSpecs.cpu || allSpecs.processor;
    if (cpuSpec) {
        displaySpecs.push({ icon: Cpu, label: 'CPU', value: cpuSpec });
    }
    
    // GPU spec
    const gpuSpec = allSpecs.gpu || allSpecs.graphics || allSpecs.vga;
    if (gpuSpec) {
        displaySpecs.push({ icon: Monitor, label: 'GPU', value: gpuSpec });
    }
    
    // RAM spec
    const ramSpec = allSpecs.ram || allSpecs.memory;
    if (ramSpec) {
        displaySpecs.push({ icon: MemoryStick, label: 'RAM', value: ramSpec });
    }
    
    // If no specific specs found, try to extract from name for PCs
    if (displaySpecs.length === 0 && (product.category === 'gaming_pc' || product.category === 'office_pc' || product.category === 'workstation')) {
        // Try to find CPU in name
        const cpuMatch = name.match(/(i[3579]-\d+|ryzen [3579] \d+|xeon)/i);
        if (cpuMatch) displaySpecs.push({ icon: Cpu, label: 'CPU', value: cpuMatch[0] });
        
        // Try to find GPU in name
        const gpuMatch = name.match(/(rtx \d+|gtx \d+|rx \d+|radeon)/i);
        if (gpuMatch) displaySpecs.push({ icon: Monitor, label: 'GPU', value: gpuMatch[0] });
        
        // Try to find RAM in name
        const ramMatch = name.match(/(\d+\s*gb\s*(ram|ddr\d)?)/i);
        if (ramMatch) displaySpecs.push({ icon: MemoryStick, label: 'RAM', value: ramMatch[0] });
    }
    
    return displaySpecs;
}

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
var PLACEHOLDER_IMG = BACKEND_URL + '/api/images/proxy?url=' + encodeURIComponent('/placeholder_product.jpg');

function proxyImg(url) {
    if (!url || url.startsWith('data:')) return url;
    if (url.startsWith(BACKEND_URL)) return url;
    return BACKEND_URL + '/api/images/proxy?url=' + encodeURIComponent(url);
}

export function ProductCard({ product, compact = false, onProductDeleted, onProductUpdated }) {
    var cartContext = useCart();
    var { isAdmin } = useAuth();
    var { isInWishlist, toggleWishlist } = useWishlist();
    var discount = calculateDiscount(product.original_price, product.price);
    var [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    var [editDialogOpen, setEditDialogOpen] = useState(false);
    var [isDeleting, setIsDeleting] = useState(false);
    var saved = isInWishlist(product.id);

    function handleToggleWishlist(e) {
        e.preventDefault();
        e.stopPropagation();
        toggleWishlist(product.id);
    }

    function handleAddToCart(e) {
        e.preventDefault();
        e.stopPropagation();
        cartContext.addToCart(product.id);
    }

    function handleViewClick(e) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = productUrl(product);
    }

    function handleEditClick(e) {
        e.preventDefault();
        e.stopPropagation();
        setEditDialogOpen(true);
    }

    function handleDeleteClick(e) {
        e.preventDefault();
        e.stopPropagation();
        setDeleteDialogOpen(true);
    }

    function handleProductSaved() {
        if (onProductUpdated) {
            onProductUpdated();
        } else {
            window.location.reload();
        }
    }

    async function handleConfirmDelete() {
        setIsDeleting(true);
        try {
            await productsAPI.delete(product.id);
            toast.success('Termék törölve!');
            setDeleteDialogOpen(false);
            if (onProductDeleted) {
                onProductDeleted(product.id);
            } else {
                // Refresh the page if no callback provided
                window.location.reload();
            }
        } catch (error) {
            toast.error('Hiba a törléskor');
        }
        setIsDeleting(false);
    }

    var imgSrc = (product.images && product.images[0]) ? proxyImg(product.images[0]) : PLACEHOLDER_IMG;

    // Compact version for related products
    if (compact) {
        return (
            <>
                <div className="group relative bg-card border border-border rounded-lg overflow-hidden card-hover" data-testid={'product-card-' + product.id}>
                    <Link to={productUrl(product)} className="block">
                        <div className="relative aspect-square overflow-hidden bg-muted">
                            <img
                                src={imgSrc}
                                alt={product.name}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                loading="lazy"
                                onError={function(e) { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                            />
                            
                            <div className="absolute top-2 left-2 flex flex-col gap-1">
                                <Badge className={cn("text-[10px] px-1.5 py-0.5 font-medium", product.condition === 'new' ? "bg-cyan-500 hover:bg-cyan-600" : "bg-amber-500 hover:bg-amber-600")}>
                                    {product.condition === 'new' ? 'Új' : 'Felúj.'}
                                </Badge>
                                {discount > 0 && (
                                    <Badge className="bg-red-500 hover:bg-red-600 text-[10px] px-1.5 py-0.5">-{discount}%</Badge>
                                )}
                            </div>

                            {/* Wishlist heart button */}
                            <button
                                onClick={handleToggleWishlist}
                                className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors"
                                title={saved ? 'Eltávolítás a mentettek közül' : 'Mentés'}
                            >
                                <Heart className={cn("w-3.5 h-3.5 transition-colors", saved ? "fill-red-500 text-red-500" : "text-muted-foreground")} />
                            </button>

                            {/* Admin controls on hover */}
                            {isAdmin && (
                                <div className="absolute top-10 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="icon" variant="secondary" className="w-6 h-6" onClick={handleEditClick} title="Szerkesztés">
                                        <Pencil className="w-3 h-3" />
                                    </Button>
                                    <Button size="icon" variant="secondary" className="w-6 h-6 text-destructive hover:text-destructive" onClick={handleDeleteClick} title="Törlés">
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Link>

                    <div className="p-2">
                        <Link to={productUrl(product)}>
                            <p className="text-[10px] text-muted-foreground mb-0.5 truncate">{product.brand}</p>
                            <h3 className="font-heading font-semibold text-xs line-clamp-2 mb-1.5 group-hover:text-primary transition-colors leading-tight">
                                {product.name}
                            </h3>
                        </Link>
                        {product.review_count > 0 && (
                            <div className="flex items-center gap-1 mb-1">
                                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                <span className="text-[10px] font-medium">{product.average_rating}</span>
                                <span className="text-[10px] text-muted-foreground">({product.review_count})</span>
                            </div>
                        )}

                        <div className="flex items-baseline gap-1">
                            <span className="font-mono font-bold text-sm text-primary">{formatPrice(product.price)}</span>
                        </div>

                        <p className={cn("text-[10px] mt-1", getStockStatus(product).color)}>
                            {getStockStatus(product).label}
                        </p>
                    </div>
                </div>

                {/* Delete confirmation dialog */}
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Termék törlése</DialogTitle>
                            <DialogDescription>
                                Biztosan törölni szeretnéd a(z) "{product.name}" terméket? Ez a művelet nem visszavonható.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Mégse</Button>
                            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                                {isDeleting ? 'Törlés...' : 'Törlés'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit modal */}
                <ProductEditModal 
                    open={editDialogOpen} 
                    onOpenChange={setEditDialogOpen} 
                    product={product}
                    onSaved={handleProductSaved}
                />
            </>
        );
    }

    return (
        <>
            <div className="group relative bg-card border border-border rounded-lg overflow-hidden card-hover" data-testid={'product-card-' + product.id}>
                <Link to={productUrl(product)} className="block">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                        <img
                            src={imgSrc}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={function(e) { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                        />
                        
                        <div className="absolute top-3 left-3 flex flex-col gap-2">
                            <Badge className={cn("text-xs font-medium", product.condition === 'new' ? "bg-cyan-500 hover:bg-cyan-600" : "bg-amber-500 hover:bg-amber-600")}>
                                {product.condition === 'new' ? 'Új' : 'Felújított'}
                            </Badge>
                            {discount > 0 && (
                                <Badge className="bg-red-500 hover:bg-red-600 text-xs">-{discount}%</Badge>
                            )}
                        </div>

                        {/* Wishlist heart button */}
                        <button
                            onClick={handleToggleWishlist}
                            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors shadow-sm"
                            title={saved ? 'Eltávolítás a mentettek közül' : 'Mentés'}
                        >
                            <Heart className={cn("w-4 h-4 transition-colors", saved ? "fill-red-500 text-red-500" : "text-muted-foreground")} />
                        </button>

                        {/* Admin controls on hover */}
                        {isAdmin && (
                            <div className="absolute top-14 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                <Button size="icon" variant="secondary" className="w-8 h-8" onClick={handleEditClick} title="Szerkesztés">
                                    <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="icon" variant="secondary" className="w-8 h-8 text-destructive hover:text-destructive" onClick={handleDeleteClick} title="Törlés">
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        )}

                        <div className="absolute bottom-3 left-3 right-3 flex gap-2 opacity-0 translate-y-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                            <Button size="sm" className="flex-1 btn-glow" onClick={handleAddToCart} data-testid={'add-to-cart-' + product.id}>
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                Kosárba
                            </Button>
                            <Button size="sm" variant="secondary" onClick={handleViewClick}>
                                <Eye className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </Link>

            <div className="p-4">
                <Link to={productUrl(product)}>
                    <p className="text-xs text-muted-foreground mb-1">{product.brand}</p>
                    <h3 className="font-heading font-semibold text-sm line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                        {product.name}
                    </h3>
                </Link>
                {product.review_count > 0 && (
                    <div className="flex items-center gap-1 mb-2">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        <span className="text-xs font-medium">{product.average_rating}</span>
                        <span className="text-xs text-muted-foreground">({product.review_count} értékelés)</span>
                    </div>
                )}
                
                {/* Display CPU, GPU, RAM specs for PCs */}
                {(() => {
                    const displaySpecs = getDisplaySpecs(product);
                    if (displaySpecs.length > 0) {
                        return (
                            <div className="space-y-1 mb-3">
                                {displaySpecs.slice(0, 3).map((spec, idx) => {
                                    const Icon = spec.icon;
                                    return (
                                        <div key={idx} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                            <Icon className="w-3 h-3 text-primary/70" />
                                            <span className="font-mono truncate">{spec.value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    } else if (product.specs) {
                        return (
                            <div className="flex flex-wrap gap-1 mb-3">
                                {Object.entries(product.specs).slice(0, 2).map(function(entry) {
                                    var key = entry[0];
                                    var value = entry[1];
                                    return (
                                        <span key={key} className="text-[10px] px-2 py-0.5 bg-secondary rounded text-muted-foreground font-mono">
                                            {typeof value === 'string' ? value : JSON.stringify(value)}
                                        </span>
                                    );
                                })}
                            </div>
                        );
                    }
                    return null;
                })()}

                <div className="flex items-baseline gap-2">
                    <span className="font-mono font-bold text-lg text-primary">{formatPrice(product.price)}</span>
                    {product.original_price && product.original_price > product.price && (
                        <span className="font-mono text-xs text-muted-foreground line-through">{formatPrice(product.original_price)}</span>
                    )}
                </div>

                <p className={cn("text-xs mt-2", getStockStatus(product).color)}>
                    {getStockStatus(product).label}
                </p>
            </div>
        </div>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Termék törlése</DialogTitle>
                    <DialogDescription>
                        Biztosan törölni szeretnéd a(z) "{product.name}" terméket? Ez a művelet nem visszavonható.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Mégse</Button>
                    <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                        {isDeleting ? 'Törlés...' : 'Törlés'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {/* Edit modal */}
        <ProductEditModal 
            open={editDialogOpen} 
            onOpenChange={setEditDialogOpen} 
            product={product}
            onSaved={handleProductSaved}
        />
    </>
    );
}

export default ProductCard;
